/**
 * pages/Dashboard.jsx
 * Story-driven SaaS dashboard with animated KPIs, sparkline cards,
 * interactive charts, and a personal greeting hero.
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Sector,
} from 'recharts';
import API from '../services/api';
import { fmtTolPlus, fmtTolMinus } from '../utils/tolerance';
import FileDetailModal from '../components/FileDetailModal';
import ShareModal from '../components/ShareModal';
import LifecycleModal from '../components/LifecycleModal';
import '../styles/dashboard.css';

/* ── UTC parse helper — backend sends naive UTC strings without 'Z' ── */
const toUTC = iso => iso ? new Date(iso.endsWith('Z') || iso.includes('+') ? iso : iso + 'Z') : null;

/* ── Helpers ─────────────────────────────────────────────────── */
function fmtDate(iso) {
  if (!iso) return '—';
  return toUTC(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function fmtTime(iso) {
  if (!iso) return '—';
  return toUTC(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function todayLabel() {
  return new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
}
function buildDailyData(history) {
  const today = new Date();
  const map = {};
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const key = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    map[key] = { date: key, scans: 0, success: 0, failed: 0 };
  }
  history.forEach((item) => {
    if (!item.upload_timestamp) return;
    const key = toUTC(item.upload_timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    if (map[key]) {
      map[key].scans   += 1;
      if (item.status === 'completed') map[key].success += 1;
      if (item.status === 'failed')    map[key].failed  += 1;
    }
  });
  return Object.values(map);
}

/* ── Count-up hook ───────────────────────────────────────────── */
function useCountUp(target, duration = 900) {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef(null);
  useEffect(() => {
    if (target == null || isNaN(Number(target))) { setDisplay(target ?? 0); return; }
    const n = Number(target);
    const start = performance.now();
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setDisplay(Math.round(eased * n));
      if (t < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafRef.current);
  }, [target, duration]);
  return display;
}

/* ── Sparkline (tiny inline area chart) ─────────────────────── */
function Sparkline({ data, color = '#F97316', height = 36 }) {
  if (!data || data.length < 2) return null;
  const max = Math.max(...data, 1);
  const w = 80, h = height;
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w;
    const y = h - (v / max) * h * 0.9;
    return `${x},${y}`;
  });
  const path = `M ${pts.join(' L ')}`;
  const fill = `M 0,${h} L ${pts.join(' L ')} L ${w},${h} Z`;
  return (
    <svg width={w} height={h} style={{ overflow: 'visible', opacity: 0.85 }}>
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      <path d={fill} fill={`url(#sg-${color.replace('#','')})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/* ── Skeleton ────────────────────────────────────────────────── */
function Skel({ w = '100%', h = 18, r = 8 }) {
  return <div className="db-skeleton" style={{ width: w, height: h, borderRadius: r }} />;
}

/* ── Animated stat card ──────────────────────────────────────── */
function StatCard({ label, value, sub, accent = '#F97316', iconBg = '#FFF7ED', icon, loading, sparkData, onClick }) {
  const animated = useCountUp(loading ? 0 : (value ?? 0));
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`db-stat-card ${hovered ? 'hovered' : ''}`}
      style={{ '--card-accent': accent, '--icon-bg': iconBg, cursor: onClick ? 'pointer' : 'default' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
    >
      {/* Top row: icon + sparkline */}
      <div className="db-stat-top">
        <div className="db-stat-icon" style={{ background: iconBg, color: accent }}>{icon}</div>
        {sparkData && !loading && (
          <div style={{ opacity: hovered ? 1 : 0.45, transition: 'opacity 0.3s', marginTop: 2 }}>
            <Sparkline data={sparkData} color={accent} height={32} />
          </div>
        )}
      </div>

      {/* Label */}
      <div className="db-stat-label">{label}</div>

      {/* Big animated number */}
      {loading
        ? <Skel h={42} w={90} r={6} />
        : <div className="db-stat-value" style={{ color: accent }}>{animated}</div>
      }

      {/* Sub text */}
      {sub && !loading && (
        <div className="db-stat-sub" style={{
          display: 'flex', alignItems: 'center', gap: 4,
          color: hovered ? accent : undefined,
          transition: 'color 0.2s',
        }}>
          {sub}
        </div>
      )}
    </div>
  );
}

/* ── Status badge ────────────────────────────────────────────── */
function StatusBadge({ status }) {
  const cls = status === 'completed' ? 'db-badge-success'
            : status === 'failed'    ? 'db-badge-error' : 'db-badge-warn';
  return <span className={`db-badge ${cls}`}>{status}</span>;
}

/* ── Custom area chart tooltip ───────────────────────────────── */
function AreaTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="db-chart-tooltip">
      <div className="db-chart-tooltip-label">{label}</div>
      {payload.map(p => (
        <div key={p.name} className="db-chart-tooltip-row">
          <span style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:8, height:8, borderRadius:'50%', background:p.color, display:'inline-block' }} />
            {p.name}
          </span>
          <strong>{p.value}</strong>
        </div>
      ))}
    </div>
  );
}

/* ── Active donut slice ──────────────────────────────────────── */
function ActiveDonutSlice(props) {
  const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill } = props;
  return (
    <Sector
      cx={cx} cy={cy}
      innerRadius={innerRadius - 4}
      outerRadius={outerRadius + 8}
      startAngle={startAngle}
      endAngle={endAngle}
      fill={fill}
    />
  );
}

/* ── Empty state ─────────────────────────────────────────────── */
function EmptyState({ message, small }) {
  return (
    <div className={`db-empty ${small ? 'db-empty-small' : ''}`}>
      <div className="db-empty-icon"><SearchIcon /></div>
      <p>{message}</p>
    </div>
  );
}

/* ══ BATCH COMBINED MODAL ══════════════════════════════════════ */
/**
 * Shows all inspection items from every file in a batch merged into
 * one read-only table. Total item count matches the dashboard batch row.
 */
function BatchCombinedModal({ files, onClose }) {
  const [results,    setResults]    = useState({}); // fileId → result data
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all(
      files
        .filter(f => f.status === 'completed')
        .map(f => API.getResults(f.id).then(d => ({ id: f.id, data: d })).catch(() => null))
    ).then(all => {
      if (cancelled) return;
      const map = {};
      for (const entry of all) {
        if (entry) map[entry.id] = entry.data;
      }
      setResults(map);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [files]);

  const allItems = useMemo(() => {
    const merged = [];
    let n = 1;
    for (const f of files) {
      const data = results[f.id];
      if (!data?.inspection_items) continue;
      const label = data.metadata?.part_no
        ? `Part ${data.metadata.part_no}`
        : f.file_name || `File ${f.id}`;
      for (const row of data.inspection_items) {
        merged.push({ ...row, _global_no: n++, _source: label });
      }
    }
    return merged;
  }, [files, results]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allItems.filter(item => {
      const typeOk = typeFilter === 'all' || (item.inspection_type || '').toLowerCase() === typeFilter;
      if (!typeOk) return false;
      if (!q) return true;
      const hay = [item.specification, item.location_on_drawing, item._source, item.inspection_type, item.inspection_tool]
        .map(v => String(v ?? '').toLowerCase()).join(' ');
      return hay.includes(q);
    });
  }, [allItems, search, typeFilter]);

  const totalCC = allItems.filter(i => i.critical_characteristic === 'Yes').length;

  const fmtLimit = v => {
    if (v == null || v === '' || v === 'NA') return '—';
    const n = parseFloat(v);
    return isNaN(n) ? v : n.toFixed(3);
  };

  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
    zIndex: 1000, display: 'flex', alignItems: 'flex-start',
    justifyContent: 'center', padding: '40px 16px', overflowY: 'auto',
  };
  const modal = {
    background: '#fff', borderRadius: 16,
    width: '100%', maxWidth: 1100,
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
    padding: '28px 28px 32px', position: 'relative',
  };

  return (
    <div style={overlay} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modal}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', color: '#1A1410', fontWeight: 700 }}>
              Combined Inspection Plan
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: '0.83rem', color: '#6b7280' }}>
              All {files.filter(f => f.status === 'completed').length} files merged —
              total matches the batch row in the dashboard
            </p>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: '#6b7280', lineHeight: 1 }}
          >
            ×
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40, color: '#9C9490' }}>Loading results…</div>
        ) : (
          <>
            {/* Metric chips */}
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
              {[
                ['Total Items',  allItems.length,  '#2563EB'],
                ['CC Symbols',   totalCC,           '#7C3AED'],
                ['Files',        files.filter(f => results[f.id]).length, '#059669'],
              ].map(([label, val, color]) => (
                <div key={label} style={{
                  background: '#F8F6F3', borderRadius: 10, padding: '10px 20px',
                  textAlign: 'center', minWidth: 100,
                }}>
                  <div style={{ fontSize: '0.72rem', color: '#9C9490', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color }}>{val}</div>
                </div>
              ))}
            </div>

            {/* Search / filter */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
              <input
                className="db-search"
                placeholder="Search all items…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex: '1 1 200px', minWidth: 160 }}
              />
              <select
                className="db-filter"
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
              >
                <option value="all">All types</option>
                <option value="dimensional">Dimensional</option>
                <option value="visual">Visual</option>
                <option value="functional">Functional</option>
              </select>
            </div>

            {/* Table */}
            <div style={{ overflowX: 'auto', border: '1px solid #E8E2D9', borderRadius: 10 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                <thead>
                  <tr style={{ background: '#1F3864', color: '#fff' }}>
                    {['#','Source File','Location','CC','Specification','Nom.','Tol+','Tol−','UCL','LCL','Type','Tool'].map(h => (
                      <th key={h} style={{ padding: '10px 10px', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap', fontSize: '0.78rem' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.length > 0 ? filtered.map((item, idx) => (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : '#FAFAF9', borderBottom: '1px solid #F0EDE8' }}>
                      <td style={{ padding: '8px 10px', color: '#9C9490' }}>{item._global_no}</td>
                      <td style={{ padding: '8px 10px', fontSize: '0.74rem', color: '#6b7280', maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item._source}
                      </td>
                      <td style={{ padding: '8px 10px' }}>{item.location_on_drawing || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 3,
                          background: item.critical_characteristic === 'Yes' ? '#ECFDF5' : 'transparent',
                          border: item.critical_characteristic === 'Yes' ? '1px solid #6EE7B7' : 'none',
                          color: item.critical_characteristic === 'Yes' ? '#059669' : '#9C9490',
                          borderRadius: 999, padding: '1px 7px', fontSize: '0.72rem', fontWeight: 700,
                        }}>
                          {item.critical_characteristic === 'Yes' ? '● Yes' : 'No'}
                        </span>
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 260, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{item.specification || '—'}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{fmtLimit(item.nominal)}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{fmtTolPlus(item.tolerance_plus)}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{fmtTolMinus(item.tolerance_minus)}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{fmtLimit(item.ucl)}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{fmtLimit(item.lcl)}</td>
                      <td style={{ padding: '8px 10px' }}>{item.inspection_type || '—'}</td>
                      <td style={{ padding: '8px 10px' }}>{item.inspection_tool || '—'}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan="12" style={{ textAlign: 'center', color: '#94a3b8', padding: 24 }}>
                        No items match your search/filter.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <p style={{ marginTop: 10, fontSize: '0.76rem', color: '#9C9490' }}>
              Read-only combined view. Open individual files to edit their inspection items.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/* ══ DASHBOARD ══════════════════════════════════════════════════ */
const PIE_COLORS = ['#059669', '#DC2626', '#D97706'];
const PAGE_SIZE  = 10;

export default function Dashboard({ onGoToWorkspace, optimisticFailed, onDataLoaded, forceRefresh = 0 }) {
  // Pre-seed from in-memory cache (eliminates skeleton flash on navigation)
  const [stats,          setStats]          = useState(() => API.getCached('stats') ?? null);
  const [history,        setHistory]        = useState(() => {
    const cached = API.getCached('history');
    if (cached) return cached;
    // Seed history with the optimistic failed scan so it shows before the API returns
    return optimisticFailed ? [optimisticFailed] : [];
  });
  const [loadingStats,   setLoadingStats]   = useState(() => !API.getCached('stats'));
  const [loadingHistory, setLoadingHistory] = useState(() => !API.getCached('history'));
  const [statsError,     setStatsError]     = useState(null);
  const [historyError,   setHistoryError]   = useState(null);
  const [search,         setSearch]         = useState('');
  const [filter,         setFilter]         = useState('all');
  const [page,           setPage]           = useState(1);
  const [selectedId,     setSelectedId]     = useState(null);
  const [activeDonut,    setActiveDonut]    = useState(0);
  const [chartKey,       setChartKey]       = useState(0); // force re-mount to retrigger animation
  const [chartType,      setChartType]      = useState('area'); // 'area' | 'bar'

  const loadStats = useCallback(async () => {
    try { setLoadingStats(true); setStatsError(null); setStats(await API.getStats()); }
    catch (e) { setStatsError(e.error || 'Could not load stats'); }
    finally { setLoadingStats(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      setHistoryError(null);
      const d = await API.getHistory(0, 200);
      setHistory(Array.isArray(d) ? d : []);
      // Real data loaded — clear optimistic placeholder in parent
      onDataLoaded?.();
    } catch (e) {
      setHistoryError(e.error || 'Could not load history');
    } finally {
      setLoadingHistory(false);
    }
  }, [onDataLoaded]);

  useEffect(() => {
    loadStats();
    loadHistory();
  }, [loadStats, loadHistory]);

  // When forceRefresh changes (e.g. after cancel/share/delete) silently reload.
  // forceRefresh === 0 is the initial value → skip to avoid double-load on mount.
  // Always bust the cache so mutations are reflected immediately.
  useEffect(() => {
    if (forceRefresh === 0) return;
    API.invalidateCache('stats', 'history');
    loadStats();
    loadHistory();
  }, [forceRefresh, loadStats, loadHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    setChartKey(k => k + 1);
    API.invalidateCache('stats', 'history');
    loadStats();
    loadHistory();
  };

  /* Update sidebar total */
  useEffect(() => {
    const el = document.getElementById('sidebar-total-scans');
    if (el && stats) el.textContent = stats.total_uploads ?? '—';
  }, [stats]);

  /* Derived */
  const successRate = stats?.total_uploads
    ? Math.round((stats.completed / stats.total_uploads) * 100) : 0;
  const animatedRate = useCountUp(loadingStats ? 0 : successRate, 1200);

  const todaysScans = history.filter(h => {
    if (!h.upload_timestamp) return false;
    return toUTC(h.upload_timestamp).toDateString() === new Date().toDateString();
  }).length;

  const dailyData  = buildDailyData(history);
  const recentActivity = history.slice(0, 5);

  const pieData = stats ? [
    { name: 'Successful', value: stats.completed },
    { name: 'Failed',     value: stats.failed },
    { name: 'Pending',    value: Math.max(0, stats.total_uploads - stats.completed - stats.failed) },
  ].filter(d => d.value > 0) : [];

  /* Sparkline data per card (last 7 days activity) */
  const scansSparkData    = dailyData.map(d => d.scans);
  const successSparkData  = dailyData.map(d => d.success);
  const failedSparkData   = dailyData.map(d => d.failed);

  /* Table */
  const filtered = history.filter(h =>
    (filter === 'all' || h.status === filter) &&
    (!search || h.file_name?.toLowerCase().includes(search.toLowerCase()))
  );
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const onSearchChange = (e) => { setSearch(e.target.value); setPage(1); };
  const onFilterChange = (e) => { setFilter(e.target.value); setPage(1); };

  const handleDelete = async (id, name, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await API.deleteHistoryItem(id);
      loadHistory();
      loadStats();
    } catch (err) {
      alert(`Could not delete "${name}": ${err?.error || 'Unknown error'}`);
    }
  };

  const [downloadOpenId, setDownloadOpenId] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState(new Set());
  const [shareBatchState, setShareBatchState] = useState(null); // { batchId, fileCount }
  const [lifecycleDrawing, setLifecycleDrawing] = useState(null); // { drawingId, drawingName }
  const [batchCombinedFiles, setBatchCombinedFiles] = useState(null); // [{id, file_name, item_count}] for combined modal

  const handleDownload = async (id, name, e) => {
    e.stopPropagation();
    const base = (name || '').toString().trim().replace(/[\\/:*?"<>|]/g, '_');
    try { await API.downloadExcel(id, `${base} - Inspection Plan.xlsx`); }
    catch {}
  };

  const handleDownloadPdf = async (id, name, e) => {
    e.stopPropagation();
    const base = (name || '').toString().trim().replace(/[\\/:*?"<>|]/g, '_');
    try { await API.downloadPdf(id, `${base} - Inspection Plan.pdf`); }
    catch {}
  };

  const handleDownloadBatchZip = async (type, fileIds, e) => {
    e.stopPropagation();
    setDownloadOpenId(null);
    try {
      if (type === 'excel') await API.downloadZipExcel(fileIds);
      else await API.downloadZipPdf(fileIds);
    } catch {}
  };

  const handleDeleteBatch = async (batchId, files, e) => {
    e.stopPropagation();
    if (!window.confirm(`Delete all ${files.length} files in this batch?`)) return;
    try {
      const results = await Promise.allSettled(files.map(f => API.deleteHistoryItem(f.id)));
      const failed  = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) {
        alert(`${failed} of ${files.length} files could not be deleted. Please try again.`);
      }
      loadHistory();
      loadStats();
    } catch (err) {
      alert(`Could not delete batch: ${err?.error || 'Unknown error'}`);
    }
  };

  const toggleBatch = (batchId, e) => {
    e.stopPropagation();
    setExpandedBatches(prev => {
      const next = new Set(prev);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  };

  const toggleDownloadMenu = (id, e) => {
    e.stopPropagation();
    setDownloadOpenId(prev => (prev === id ? null : id));
  };

  // Close dropdown when clicking anywhere else
  const dashDownloadRef = useRef(null);
  useEffect(() => {
    if (!downloadOpenId) return;
    const handler = (e) => {
      if (!e.target.closest('[data-dl-menu]')) setDownloadOpenId(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [downloadOpenId]);

  return (
    <div className="db-root">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <div className="db-hero">
        <div className="db-hero-text">
          <div className="db-hero-date">
            <span className="db-hero-date-dot" />
            {todayLabel()}
          </div>
          <h1 className="db-hero-title">
            {loadingStats ? 'Welcome back.' : (
              stats?.total_uploads
                ? <>{`You've analysed `}<span>{stats.total_uploads}</span>{` drawing${stats.total_uploads !== 1 ? 's' : ''}.`}</>
                : 'Ready to analyse your first drawing.'
            )}
          </h1>
          <p className="db-hero-sub">
            {loadingStats ? 'Loading your workspace...' : (
              stats?.total_uploads
                ? `${stats.completed} completed · ${stats.total_cc_symbols} critical characteristics · ${stats.total_inspection_items} inspection items generated`
                : 'Upload an engineering drawing and get a professional inspection plan in seconds.'
            )}
          </p>
        </div>
        <div className="db-hero-actions">
          {!loadingStats && stats?.total_uploads > 0 && (
            <div className="db-hero-quick-stats">
              <div className="db-hero-qstat">
                <span className="db-hero-qstat-val">{todaysScans}</span>
                <span className="db-hero-qstat-lbl">Today</span>
              </div>
              <div className="db-hero-qstat-div" />
              <div className="db-hero-qstat">
                <span className="db-hero-qstat-val" style={{ color: successRate >= 80 ? 'var(--success)' : 'var(--warning)' }}>{successRate}%</span>
                <span className="db-hero-qstat-lbl">Success rate</span>
              </div>
            </div>
          )}
          <button className="db-btn-primary" onClick={onGoToWorkspace}>
            <PlusIcon /> New Analysis
          </button>
        </div>
      </div>

      {/* ── STAT CARDS ───────────────────────────────────────── */}
      <div className="db-stats-row">
        <StatCard
          label="Total Scans" value={stats?.total_uploads} sub="All time"
          accent="#F97316" iconBg="#FFF7ED" icon={<ScanIcon />}
          loading={loadingStats} sparkData={scansSparkData}
        />
        <StatCard
          label="Successful" value={stats?.completed} sub={`${successRate}% success rate`}
          accent="#059669" iconBg="#ECFDF5" icon={<CheckIcon />}
          loading={loadingStats} sparkData={successSparkData}
        />
        <StatCard
          label="Failed" value={stats?.failed} sub="Need attention"
          accent="#DC2626" iconBg="#FEF2F2" icon={<XIcon />}
          loading={loadingStats} sparkData={failedSparkData}
        />
        <StatCard
          label="Pending" value={stats ? Math.max(0, (stats.total_uploads || 0) - (stats.completed || 0) - (stats.failed || 0)) : undefined}
          sub="Awaiting processing"
          accent="#D97706" iconBg="#FFFBEB" icon={<ClockIcon />}
          loading={loadingStats}
        />
        <StatCard
          label="Inspection Items" value={stats?.total_inspection_items} sub="Generated total"
          accent="#2563EB" iconBg="#EFF6FF" icon={<ListIcon />}
          loading={loadingStats}
        />
      </div>

      {statsError && <div className="db-error-banner" style={{ margin: 0 }}>{statsError}</div>}

      {/* ── CHARTS ───────────────────────────────────────────── */}
      <div className="db-charts-row">

        {/* Activity chart — area or bar */}
        <div className="db-card">
          <div className="db-card-header">
            <div>
              <h3>Activity Overview</h3>
              <p>Daily scan volume — last 7 days</p>
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Legend */}
              <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                {[['#F97316','Total'],['#059669','Success'],['#DC2626','Failed']].map(([c,l]) => (
                  <span key={l} style={{ display:'flex', alignItems:'center', gap:5, fontSize:'0.75rem', color:'var(--text-secondary)', fontWeight:600 }}>
                    <span style={{ width:10, height:10, borderRadius:3, background:c, display:'inline-block', flexShrink:0 }} />
                    {l}
                  </span>
                ))}
              </div>
              {/* Toggle */}
              <div className="db-chart-toggle">
                <button className={`db-chart-toggle-btn ${chartType==='area'?'active':''}`} onClick={() => setChartType('area')}>Area</button>
                <button className={`db-chart-toggle-btn ${chartType==='bar'?'active':''}`}  onClick={() => setChartType('bar')}>Bar</button>
              </div>
              <button className="db-btn-ghost" style={{ padding:'0.32rem 0.65rem', fontSize:'0.74rem' }} onClick={handleRefresh}>↺</button>
            </div>
          </div>
          <div className="db-card-body">
            {loadingHistory ? <Skel h={230} r={12} /> : (
              <ResponsiveContainer width="100%" height={230} key={`${chartKey}-${chartType}`}>
                {chartType === 'area' ? (
                  <AreaChart data={dailyData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                    <defs>
                      {[['gO','#F97316'],['gG','#059669'],['gR','#DC2626']].map(([id,c]) => (
                        <linearGradient key={id} id={id} x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%"  stopColor={c} stopOpacity={0.22}/>
                          <stop offset="95%" stopColor={c} stopOpacity={0.01}/>
                        </linearGradient>
                      ))}
                    </defs>
                    <CartesianGrid strokeDasharray="4 4" stroke="#EDE8E1" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill:'#9C9490', fontSize:11, fontWeight:500 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill:'#9C9490', fontSize:11 }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
                    <Tooltip content={<AreaTip />} cursor={{ stroke:'#FDBA74', strokeWidth:1, strokeDasharray:'4 2' }} />
                    <Area type="monotone" dataKey="scans"   stroke="#F97316" fill="url(#gO)" strokeWidth={2.5} name="Total"   dot={{ r:3, fill:'#F97316', strokeWidth:0 }} activeDot={{ r:5, fill:'#F97316', strokeWidth:2, stroke:'#fff' }} />
                    <Area type="monotone" dataKey="success" stroke="#059669" fill="url(#gG)" strokeWidth={2}   name="Success" dot={false} activeDot={{ r:5, fill:'#059669', strokeWidth:2, stroke:'#fff' }} />
                    <Area type="monotone" dataKey="failed"  stroke="#DC2626" fill="url(#gR)" strokeWidth={2}   name="Failed"  dot={false} activeDot={{ r:5, fill:'#DC2626', strokeWidth:2, stroke:'#fff' }} />
                  </AreaChart>
                ) : (
                  <BarChart data={dailyData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barCategoryGap="28%">
                    <CartesianGrid strokeDasharray="4 4" stroke="#EDE8E1" vertical={false} />
                    <XAxis dataKey="date" tick={{ fill:'#9C9490', fontSize:11, fontWeight:500 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill:'#9C9490', fontSize:11 }} axisLine={false} tickLine={false} allowDecimals={false} width={28} />
                    <Tooltip content={<AreaTip />} cursor={{ fill:'rgba(249,115,22,0.04)' }} />
                    <Bar dataKey="scans"   fill="#F97316" name="Total"   radius={[4,4,0,0]} maxBarSize={24} opacity={0.9} />
                    <Bar dataKey="success" fill="#059669" name="Success" radius={[4,4,0,0]} maxBarSize={24} opacity={0.9} />
                    <Bar dataKey="failed"  fill="#DC2626" name="Failed"  radius={[4,4,0,0]} maxBarSize={24} opacity={0.9} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Donut */}
        <div className="db-card">
          <div className="db-card-header">
            <div>
              <h3>Success Rate</h3>
              <p>All-time distribution</p>
            </div>
          </div>
          <div className="db-card-body">
            {loadingStats ? (
              <div style={{ display:'flex', justifyContent:'center' }}><Skel h={180} w={180} r={999} /></div>
            ) : pieData.length === 0 ? (
              <EmptyState message="No data yet" small />
            ) : (
              <>
                <div className="db-donut-wrap">
                  <ResponsiveContainer width="100%" height={188}>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%" cy="50%"
                        innerRadius={54} outerRadius={76}
                        paddingAngle={3} dataKey="value"
                        strokeWidth={0}
                        activeIndex={activeDonut}
                        activeShape={ActiveDonutSlice}
                        onMouseEnter={(_, idx) => setActiveDonut(idx)}
                      >
                        {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="db-donut-center">
                    <span className="db-donut-pct" style={{ color: successRate >= 80 ? 'var(--success)' : successRate >= 50 ? 'var(--warning)' : 'var(--error)' }}>
                      {animatedRate}%
                    </span>
                    <span className="db-donut-sub">success</span>
                  </div>
                </div>
                <div className="db-chart-legend">
                  {pieData.map((d, i) => (
                    <div
                      key={d.name}
                      className="db-chart-legend-item"
                      style={{ cursor:'pointer', opacity: activeDonut === i ? 1 : 0.65, transition:'opacity 0.2s' }}
                      onMouseEnter={() => setActiveDonut(i)}
                    >
                      <div className="db-legend-dot-label">
                        <span className="db-legend-dot" style={{ background: PIE_COLORS[i] }} />
                        <span className="db-legend-name">{d.name}</span>
                      </div>
                      <span className="db-legend-val" style={{ color: PIE_COLORS[i] }}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ── RECENT ACTIVITY ──────────────────────────────────── */}
      <div className="db-card">
        <div className="db-card-header">
          <div>
            <h3>Recent Activity</h3>
            <p>Your last {recentActivity.length} uploaded drawings</p>
          </div>
          <button className="db-btn-ghost" onClick={handleRefresh}>↺ Refresh</button>
        </div>
        {loadingHistory ? (
          <div className="db-loading-rows">{[...Array(4)].map((_,i) => <Skel key={i} h={56} r={0} />)}</div>
        ) : recentActivity.length === 0 ? (
          <EmptyState message="No drawings uploaded yet. Start your first analysis." />
        ) : (
          <div className="db-activity-list">
            {recentActivity.map((item) => {
              /* ── Batch item ─────────────────────────────────────── */
              if (item.is_batch) {
                const isExpanded     = expandedBatches.has(item.batch_id);
                const completedFiles = (item.files || []).filter(f => f.status === 'completed');
                const allFileIds     = completedFiles.map(f => f.id);
                const raZipKey       = `ra-batch-${item.batch_id}`;
                return (
                  <React.Fragment key={`batch-ra-${item.batch_id}`}>
                    {/* Batch header row */}
                    <div
                      className="db-activity-row"
                      style={{ background: '#FFFBF5', cursor: 'pointer' }}
                      onClick={e => toggleBatch(item.batch_id, e)}
                    >
                      <div className="db-activity-icon" style={{ color: '#B45309' }}>
                        <span style={{ fontSize: 13, userSelect: 'none' }}>{isExpanded ? '▼' : '▶'}</span>
                      </div>
                      <div className="db-activity-info">
                        <div className="db-activity-name" style={{ color: '#92400E', display: 'flex', alignItems: 'center', gap: 6 }}>
                          {item.display_name || item.file_name}
                          <span style={{ fontSize: 10, background: '#FEF3C7', color: '#B45309', border: '1px solid #FDE68A', borderRadius: 4, padding: '1px 5px', fontWeight: 700 }}>
                            {item.file_count} files
                          </span>
                        </div>
                        <div className="db-activity-meta">
                          {fmtTime(item.upload_timestamp)}
                          {item.cc_count != null && (
                            <><span className="db-activity-dot">·</span><span>{item.cc_count} CCs · {item.item_count} items</span></>
                          )}
                        </div>
                      </div>
                      <StatusBadge status={item.status} />
                      <div className="db-activity-actions" onClick={e => e.stopPropagation()}>
                        {/* ZIP download for completed batches */}
                        {item.status === 'completed' && allFileIds.length > 0 && (
                          <div style={{ position: 'relative' }} data-dl-menu>
                            <button
                              className="db-action-btn db-action-download"
                              onClick={e => toggleDownloadMenu(raZipKey, e)}
                            >
                              ↓ ZIP ▾
                            </button>
                            {downloadOpenId === raZipKey && (
                              <div style={{
                                position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                                background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 170, overflow: 'hidden',
                              }} data-dl-menu>
                                <button style={{ display: 'block', width: '100%', padding: '8px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                  onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                                  onClick={e => handleDownloadBatchZip('excel', allFileIds, e)}>
                                  All Excel (.zip)
                                </button>
                                <button style={{ display: 'block', width: '100%', padding: '8px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                  onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                  onMouseOut={e => e.currentTarget.style.background = 'none'}
                                  onClick={e => handleDownloadBatchZip('pdf', allFileIds, e)}>
                                  All PDF (.zip)
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Expanded child file rows */}
                    {isExpanded && (item.files || []).map((file) => {
                      const raFileKey = `ra-f-${file.id}`;
                      return (
                        <div
                          key={`ra-file-${file.id}`}
                          className="db-activity-row"
                          style={{ background: '#FDF8F3', borderLeft: '3px solid #F97316', paddingLeft: 8, cursor: 'pointer' }}
                          onClick={() => setSelectedId(file.id)}
                        >
                          <div className="db-activity-icon" style={{ color: '#B45309', fontSize: 11 }}>└</div>
                          <div className="db-activity-info">
                            <div className="db-activity-name" style={{ fontSize: 13 }}>{file.display_name || file.file_name}</div>
                            <div className="db-activity-meta">
                              {fmtTime(file.upload_timestamp)}
                              {file.cc_count != null && (
                                <><span className="db-activity-dot">·</span><span>{file.cc_count} CCs · {file.item_count} items</span></>
                              )}
                            </div>
                          </div>
                          <StatusBadge status={file.status} />
                          <div className="db-activity-actions" onClick={e => e.stopPropagation()}>
                            <button className="db-action-btn" onClick={() => setSelectedId(file.id)}>View</button>
                            {file.status === 'completed' && (
                              <div style={{ position: 'relative' }} data-dl-menu>
                                <button
                                  className="db-action-btn db-action-download"
                                  onClick={e => toggleDownloadMenu(raFileKey, e)}
                                >
                                  ↓ ▾
                                </button>
                                {downloadOpenId === raFileKey && (
                                  <div style={{
                                    position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 150, overflow: 'hidden',
                                  }} data-dl-menu>
                                    <button style={{ display: 'block', width: '100%', padding: '8px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                      onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                                      onClick={e => { setDownloadOpenId(null); handleDownload(file.id, file.display_name || file.file_name, e); }}>
                                      Excel (.xlsx)
                                    </button>
                                    <button style={{ display: 'block', width: '100%', padding: '8px 14px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                      onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                                      onClick={e => { setDownloadOpenId(null); handleDownloadPdf(file.id, file.display_name || file.file_name, e); }}>
                                      PDF (.pdf)
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </React.Fragment>
                );
              }

              /* ── Single file item ───────────────────────────────── */
              const raKey = `ra-${item.id}`;
              return (
                <div key={item.id} className="db-activity-row" onClick={() => setSelectedId(item.id)}>
                  <div className="db-activity-icon"><FileIcon /></div>
                  <div className="db-activity-info">
                    <div className="db-activity-name">{item.display_name || item.file_name}</div>
                    <div className="db-activity-meta">
                      {fmtTime(item.upload_timestamp)}
                      {item.cc_count != null && (
                        <><span className="db-activity-dot">·</span><span>{item.cc_count} CCs · {item.item_count} items</span></>
                      )}
                    </div>
                  </div>
                  <StatusBadge status={item.status} />
                  <div className="db-activity-actions" onClick={e => e.stopPropagation()}>
                    <button className="db-action-btn" onClick={() => setSelectedId(item.id)}>View</button>
                    {item.status === 'completed' && (
                      <div style={{ position: 'relative' }} data-dl-menu>
                        <button
                          className="db-action-btn db-action-download"
                          onClick={e => toggleDownloadMenu(raKey, e)}
                        >
                          ↓ Report ▾
                        </button>
                        {downloadOpenId === raKey && (
                          <div style={{
                            position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 150, overflow: 'hidden',
                          }} data-dl-menu>
                            <button style={{ display: 'block', width: '100%', padding: '8px 14px',
                              textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                              onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                              onMouseOut={e => e.currentTarget.style.background = 'none'}
                              onClick={e => { setDownloadOpenId(null); handleDownload(item.id, item.display_name || item.file_name, e); }}>
                              Excel (.xlsx)
                            </button>
                            <button style={{ display: 'block', width: '100%', padding: '8px 14px',
                              textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                              onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                              onMouseOut={e => e.currentTarget.style.background = 'none'}
                              onClick={e => { setDownloadOpenId(null); handleDownloadPdf(item.id, item.display_name || item.file_name, e); }}>
                              PDF (.pdf)
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── ALL SCANS TABLE ───────────────────────────────────── */}
      <div className="db-card">
        <div className="db-card-header">
          <div>
            <h3>All Scans</h3>
            <p>{filtered.length} record{filtered.length !== 1 ? 's' : ''} {filter !== 'all' || search ? '(filtered)' : 'total'}</p>
          </div>
          <div className="db-table-controls">
            <input className="db-search" placeholder="Search by file name..." value={search} onChange={onSearchChange} />
            <select className="db-filter" value={filter} onChange={onFilterChange}>
              <option value="all">All statuses</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
            </select>
          </div>
        </div>

        {historyError && <div className="db-error-banner">{historyError}</div>}

        {loadingHistory ? (
          <div className="db-loading-rows">{[...Array(6)].map((_,i) => <Skel key={i} h={52} r={0} />)}</div>
        ) : paginated.length === 0 ? (
          <EmptyState message={search || filter !== 'all' ? 'No matching records.' : 'No scans yet. Upload your first drawing to get started.'} />
        ) : (
          <>
            <div className="db-table-wrap">
              <table className="db-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>File Name</th>
                    <th>Uploaded</th>
                    <th>Status</th>
                    <th>Approval</th>
                    <th>CC Symbols</th>
                    <th>Items</th>
                    <th>Completed</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.map((item, idx) => {
                    // ── Batch row ────────────────────────────────────────────
                    if (item.is_batch) {
                      const isExpanded = expandedBatches.has(item.batch_id);
                      const completedFiles = (item.files || []).filter(f => f.status === 'completed');
                      const allFileIds = completedFiles.map(f => f.id);
                      return [
                        <tr key={`batch-${item.batch_id}`} style={{ background: '#FFFBF5', cursor: 'pointer' }}
                          onClick={e => toggleBatch(item.batch_id, e)}>
                          <td className="db-cell-muted">{(page-1)*PAGE_SIZE + idx + 1}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 13, color: '#B45309', userSelect: 'none' }}>
                                {isExpanded ? '▼' : '▶'}
                              </span>
                              <div>
                                <div className="db-filename" style={{ color: '#92400E' }}>
                                  {item.display_name || item.file_name || `Batch (${item.file_count} files)`}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="db-cell-muted">{fmtDate(item.upload_timestamp)}</td>
                          <td><StatusBadge status={item.status} /></td>
                          <td>
                            <ApprovalBadge
                              status={item.approval_status}
                              reviewerName={item.reviewer_name}
                              reviewerEmail={item.reviewer_email}
                              reviewers={item.reviewers || []}
                            />
                          </td>
                          <td><span className="db-cell-accent">{item.cc_count ?? '—'}</span></td>
                          <td className="db-cell-muted">{item.item_count ?? '—'}</td>
                          <td className="db-cell-muted">{item.completed_timestamp ? fmtDate(item.completed_timestamp) : '—'}</td>
                          <td onClick={e => e.stopPropagation()}>
                            <div className="db-row-actions">
                              {item.status === 'completed' && completedFiles.length > 0 && (
                                <button
                                  className="db-action-btn"
                                  style={{ fontSize: 11, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}
                                  onClick={e => { e.stopPropagation(); setBatchCombinedFiles(completedFiles); }}
                                  title="View all inspection items from all files combined"
                                >
                                  View All Items
                                </button>
                              )}
                              {item.status === 'completed' && !['approved', 'self_approved'].includes(item.approval_status) && (
                                <button className="db-action-btn" style={{ fontSize: 11, background: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }}
                                  onClick={e => { e.stopPropagation(); setShareBatchState({ batchId: item.batch_id, fileCount: item.file_count, displayName: item.display_name || item.file_name }); }}>
                                  {item.approval_status === 'pending_review' ? 'Reshare ↺' : 'Ask Approval'}
                                </button>
                              )}
                              {item.status === 'completed' && allFileIds.length > 0 && (
                                <div style={{ position: 'relative' }} data-dl-menu>
                                  <button
                                    className="db-action-btn db-action-download"
                                    onClick={e => toggleDownloadMenu(`batch-${item.batch_id}`, e)}
                                  >
                                    ↓ ZIP ▾
                                  </button>
                                  {downloadOpenId === `batch-${item.batch_id}` && (
                                    <div style={{
                                      position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                                      background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 170, overflow: 'hidden',
                                    }} data-dl-menu>
                                      <button style={{ display: 'block', width: '100%', padding: '8px 14px',
                                        textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                        onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                        onMouseOut={e => e.currentTarget.style.background = 'none'}
                                        onClick={e => handleDownloadBatchZip('excel', allFileIds, e)}>
                                        All Excel (.zip)
                                      </button>
                                      <button style={{ display: 'block', width: '100%', padding: '8px 14px',
                                        textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                        onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                        onMouseOut={e => e.currentTarget.style.background = 'none'}
                                        onClick={e => handleDownloadBatchZip('pdf', allFileIds, e)}>
                                        All PDF (.zip)
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                              <button className="db-action-btn db-action-delete"
                                onClick={e => handleDeleteBatch(item.batch_id, item.files || [], e)}>
                                Del
                              </button>
                            </div>
                          </td>
                        </tr>,
                        // ── Child file rows (visible when expanded) ──────────
                        ...(!isExpanded ? [] : (item.files || []).map((file, fi) => (
                          <tr key={`batch-file-${file.id}`}
                            style={{ background: '#FDF8F3', borderLeft: '3px solid #F97316' }}
                            onClick={() => setSelectedId(file.id)}>
                            <td className="db-cell-muted" style={{ paddingLeft: 28, fontSize: 11, color: '#B45309' }}>
                              └ {fi + 1}
                            </td>
                            <td>
                              <div className="db-filename" style={{ paddingLeft: 12, fontSize: 13 }}>
                                {file.display_name || file.file_name}
                              </div>
                            </td>
                            <td className="db-cell-muted">{fmtDate(file.upload_timestamp)}</td>
                            <td><StatusBadge status={file.status} /></td>
                            <td>
                              <ApprovalBadge
                                status={file.approval_status}
                                reviewerName={file.reviewer_name}
                                reviewerEmail={file.reviewer_email}
                                reviewers={file.reviewers || []}
                              />
                            </td>
                            <td><span className="db-cell-accent">{file.cc_count ?? '—'}</span></td>
                            <td className="db-cell-muted">{file.item_count ?? '—'}</td>
                            <td className="db-cell-muted">{file.completed_timestamp ? fmtDate(file.completed_timestamp) : '—'}</td>
                            <td onClick={e => e.stopPropagation()}>
                              <div className="db-row-actions">
                                <button className="db-action-btn" onClick={() => setSelectedId(file.id)}>View</button>
                                {file.status === 'completed' && (
                                  <div style={{ position: 'relative' }} data-dl-menu>
                                    <button
                                      className="db-action-btn db-action-download"
                                      onClick={e => toggleDownloadMenu(`tf-${file.id}`, e)}
                                    >
                                      ↓ ▾
                                    </button>
                                    {downloadOpenId === `tf-${file.id}` && (
                                      <div style={{
                                        position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                                        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 150, overflow: 'hidden',
                                      }} data-dl-menu>
                                        <button style={{ display: 'block', width: '100%', padding: '8px 14px',
                                          textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                          onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                          onMouseOut={e => e.currentTarget.style.background = 'none'}
                                          onClick={e => { setDownloadOpenId(null); handleDownload(file.id, file.display_name || file.file_name, e); }}>
                                          Excel (.xlsx)
                                        </button>
                                        <button style={{ display: 'block', width: '100%', padding: '8px 14px',
                                          textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                          onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                          onMouseOut={e => e.currentTarget.style.background = 'none'}
                                          onClick={e => { setDownloadOpenId(null); handleDownloadPdf(file.id, file.display_name || file.file_name, e); }}>
                                          PDF (.pdf)
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                )}
                                <button className="db-action-btn db-action-delete"
                                  onClick={e => handleDelete(file.id, file.file_name, e)}>Del</button>
                                <button
                                  className="db-action-btn"
                                  style={{ fontSize: 11, background: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE' }}
                                  onClick={e => { e.stopPropagation(); setLifecycleDrawing({ drawingId: file.id, drawingName: file.display_name || file.file_name }); }}
                                >
                                  Lifecycle
                                </button>
                              </div>
                            </td>
                          </tr>
                        )))
                      ];
                    }

                    // ── Solo (single-file) row ────────────────────────────────
                    return (
                      <tr key={item.id} onClick={() => setSelectedId(item.id)}>
                        <td className="db-cell-muted">{(page-1)*PAGE_SIZE + idx + 1}</td>
                        <td>
                          <div className="db-filename" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {item.display_name || item.file_name}
                            {item.scan_type === 'tabular' && (
                              <span style={{ fontSize: 10, fontWeight: 700, color: '#1d4ed8', background: '#dbeafe', borderRadius: 4, padding: '1px 6px', letterSpacing: 0.3 }}>TABULAR</span>
                            )}
                          </div>
                        </td>
                        <td className="db-cell-muted">{fmtDate(item.upload_timestamp)}</td>
                        <td><StatusBadge status={item.status} /></td>
                        <td>
                          <ApprovalBadge
                            status={item.approval_status}
                            reviewerName={item.reviewer_name}
                            reviewerEmail={item.reviewer_email}
                            reviewers={item.reviewers || []}
                          />
                        </td>
                        <td><span className="db-cell-accent">{item.cc_count ?? '—'}</span></td>
                        <td className="db-cell-muted">{item.item_count ?? '—'}</td>
                        <td className="db-cell-muted">{item.completed_timestamp ? fmtDate(item.completed_timestamp) : '—'}</td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="db-row-actions">
                            <button className="db-action-btn" onClick={() => setSelectedId(item.id)}>View</button>
                            {item.status === 'completed' && (
                              <div style={{ position: 'relative' }} data-dl-menu>
                                <button
                                  className="db-action-btn db-action-download"
                                  onClick={e => toggleDownloadMenu(`t-${item.id}`, e)}
                                >
                                  ↓ Report ▾
                                </button>
                                {downloadOpenId === `t-${item.id}` && (
                                  <div style={{
                                    position: 'absolute', top: 'calc(100% + 4px)', right: 0, zIndex: 200,
                                    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 150, overflow: 'hidden',
                                  }} data-dl-menu>
                                    <button style={{ display: 'block', width: '100%', padding: '8px 14px',
                                      textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                      onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                                      onClick={e => { setDownloadOpenId(null); handleDownload(item.id, item.display_name || item.file_name, e); }}>
                                      Excel (.xlsx)
                                    </button>
                                    <button style={{ display: 'block', width: '100%', padding: '8px 14px',
                                      textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}
                                      onMouseOver={e => e.currentTarget.style.background = '#f1f5f9'}
                                      onMouseOut={e => e.currentTarget.style.background = 'none'}
                                      onClick={e => { setDownloadOpenId(null); handleDownloadPdf(item.id, item.display_name || item.file_name, e); }}>
                                      PDF (.pdf)
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                            <button className="db-action-btn db-action-delete" onClick={e => handleDelete(item.id, item.file_name, e)}>Del</button>
                            <button
                              className="db-action-btn"
                              style={{ fontSize: 11, background: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE' }}
                              onClick={e => { e.stopPropagation(); setLifecycleDrawing({ drawingId: item.id, drawingName: item.display_name || item.file_name }); }}
                            >
                              Lifecycle
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="db-pagination">
                <button className="db-page-btn" disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
                {[...Array(Math.min(totalPages,7))].map((_,i) => (
                  <button key={i} className={`db-page-btn ${page===i+1?'active':''}`} onClick={() => setPage(i+1)}>{i+1}</button>
                ))}
                <button className="db-page-btn" disabled={page===totalPages} onClick={() => setPage(p=>p+1)}>Next →</button>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── MODAL ────────────────────────────────────────────── */}
      {selectedId && (
        <FileDetailModal
          fileId={selectedId}
          onClose={() => setSelectedId(null)}
          onDownload={(id, filename) => API.downloadExcel(id, filename)}
        />
      )}

      {/* ── BATCH COMBINED MODAL ─────────────────────────────── */}
      {batchCombinedFiles && (
        <BatchCombinedModal
          files={batchCombinedFiles}
          onClose={() => setBatchCombinedFiles(null)}
        />
      )}

      {/* ── BATCH SHARE MODAL ────────────────────────────────── */}
      {shareBatchState && (
        <ShareModal
          drawingId={null}
          batchId={shareBatchState.batchId}
          fileCount={shareBatchState.fileCount}
          drawingName={shareBatchState.displayName || `Batch (${shareBatchState.fileCount} files)`}
          onDone={() => { setShareBatchState(null); loadHistory(); loadStats(); }}
          onCancel={() => setShareBatchState(null)}
        />
      )}

      {/* ── LIFECYCLE MODAL ──────────────────────────────────── */}
      {lifecycleDrawing && (
        <LifecycleModal
          drawingId={lifecycleDrawing.drawingId}
          drawingName={lifecycleDrawing.drawingName}
          onClose={() => setLifecycleDrawing(null)}
        />
      )}
    </div>
  );
}

/* ── Icons ───────────────────────────────────────────────────── */
function PlusIcon()   { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function ScanIcon()   { return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/><line x1="15" y1="3" x2="15" y2="21"/></svg>; }
function CheckIcon()  { return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/></svg>; }
function XIcon()      { return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>; }
function StarIcon()   { return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>; }
function ListIcon()   { return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>; }
function ClockIcon()  { return <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15.5 14"/></svg>; }
function FileIcon()   { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>; }
function SearchIcon() { return <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }

/* ── ApprovalBadge ───────────────────────────────────────────── */
function ApprovalBadge({ status, reviewerName, reviewerEmail, reviewers = [] }) {
  const [expanded, setExpanded] = React.useState(false);

  const map = {
    pending:        { label: '—',              bg: 'transparent', color: '#9C9490' },
    pending_review: { label: 'Pending Review', bg: '#FFF9EB',     color: '#B45309' },
    approved:       { label: 'Approved',       bg: '#ECFDF5',     color: '#065F46' },
    rejected:       { label: 'Rejected',       bg: '#FEF2F2',     color: '#991B1B' },
    self_approved:  { label: 'Self Approved',  bg: '#F0FDF4',     color: '#166534' },
  };
  const statusDot = {
    pending:       { label: 'Pending',   color: '#B45309' },
    approved:      { label: 'Approved',  color: '#065F46' },
    rejected:      { label: 'Rejected',  color: '#991B1B' },
    self_approved: { label: 'Self',      color: '#166534' },
  };

  const cfg = map[status] || map.pending;
  if (cfg.label === '—') return <span style={{ color: '#BDB9B5', fontSize: '0.8rem' }}>—</span>;

  const multiReviewer = reviewers && reviewers.length > 1;

  const fallbackLine = (status === 'approved' || status === 'rejected') && (reviewerName || reviewerEmail)
    ? (reviewerName ? `${reviewerName}${reviewerEmail ? ` (${reviewerEmail})` : ''}` : reviewerEmail)
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
        <span style={{
          display: 'inline-block',
          padding: '2px 10px',
          borderRadius: 20,
          fontSize: '0.73rem',
          fontWeight: 600,
          backgroundColor: cfg.bg,
          color: cfg.color,
          whiteSpace: 'nowrap',
        }}>
          {cfg.label}
        </span>
        {multiReviewer && (
          <button
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            style={{
              background: 'none', border: '1px solid #E8E2DC',
              borderRadius: 10, padding: '1px 7px',
              fontSize: '0.68rem', color: '#5C5450',
              cursor: 'pointer', whiteSpace: 'nowrap',
            }}
            title={expanded ? 'Collapse reviewers' : 'Show all reviewers'}
          >
            {reviewers.length} reviewers {expanded ? '▲' : '▼'}
          </button>
        )}
      </div>

      {/* Multi-reviewer expanded list */}
      {multiReviewer && expanded && (
        <div style={{
          marginTop: 4, padding: '6px 8px',
          background: '#FAFAF8', border: '1px solid #EDE8E3',
          borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 4,
        }}>
          {reviewers.map((r, i) => {
            const ds = statusDot[r.status] || statusDot.pending;
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.69rem', color: '#3D3530' }}>
                <span style={{
                  width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
                  backgroundColor: ds.color,
                }} />
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.name ? `${r.name} (${r.email})` : r.email}
                </span>
                <span style={{ color: ds.color, fontWeight: 600, flexShrink: 0 }}>{ds.label}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Single reviewer fallback line */}
      {!multiReviewer && fallbackLine && (
        <span style={{ fontSize: '0.69rem', color: '#6B6360', lineHeight: 1.3 }}>
          {status === 'approved' ? 'Approved by' : status === 'rejected' ? 'Rejected by' : null} {fallbackLine}
        </span>
      )}
    </div>
  );
}
